import BaseButton from "Components/Base/BaseButton";
import { useNavigate } from "react-router-dom";
import { CreditNoteLabel } from 'Components/constants/CreditNote'
import { NewLabel } from "Components/constants/PartsIssueDetails";
import { useEffect, useMemo, useState } from "react";
import { listOfCreditNote } from "Api/creditNote";
import { checkStatusCodeSuccess, NormalDate, notFound, rupeeSymbol, Search } from "Components/constants/Common";
import { toast } from "react-toastify";
import { WorkOrderKey, WorkOrderLabel } from "Components/constants/WorkOrder";
import { ActionKey, ActionLabel, editLabel } from "Components/constants/Role";
import { RiSearchLine } from "react-icons/ri";
import TableContainer from "Components/Base/BaseTable";

const CreditNote = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [searchValue, setSearchValue] = useState("");
    const [creditNote, setCreditNote] = useState<any[]>([]);
    const [searchCreditNote, setSearchCreditNote] = useState<Array<any>>([]);
    const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
    const [columnName, setColumnName] = useState("id");
    const [sortOrder, setSortOrder] = useState("desc");
    const [loader, setLoader] = useState<boolean>(false);

    document.title = CreditNoteLabel.CreditNoteOrderTitle;

    const fetchCreditNote = async () => {
        const payload = {
            pageSize,
            page: currentPage,
            sortKey: columnName,
            sortValue: sortOrder,
        };
        setLoader(true);
        listOfCreditNote(payload)
            .then((res) => {
                if (checkStatusCodeSuccess(res?.statusCode)) {
                    setCreditNote(res?.data?.creditNote || []);
                    setTotalRecords(res?.data?.totalItems || 0);
                    setTotalPages(res?.data?.totalPages);
                    setCurrentPage(res?.data?.currentPage);
                    setTotalNumberOfRows(res?.data?.numberOfRows);
                    toast.success(res?.message);
                } else {
                    setCreditNote([]);
                }
            })
            .catch(() => {
                setCreditNote([]);
            })
            .finally(() => setLoader(false));
    };

    const fetchSearchCreditNoteData = () => {
        const payload = {
            pageSize,
            page: currentPage,
            search: searchValue,
            sortKey: columnName,
            sortValue: sortOrder,
        };
        setLoader(true);
        listOfCreditNote(payload)
            .then((res) => {
                if (checkStatusCodeSuccess(res?.statusCode)) {
                    setSearchCreditNote(res?.data?.creditNote || []);
                    setTotalRecords(res?.data?.totalItems || 0);
                    setTotalPages(res?.data?.totalPages);
                    setCurrentPage(res?.data?.currentPage);
                    setTotalNumberOfRows(res?.data?.numberOfRows);
                    toast.success(res?.message);
                } else {
                    setSearchCreditNote([]);
                }
            })
            .catch(() => {
                setSearchCreditNote([]);
            })
            .finally(() => setLoader(false));
    };

    useEffect(() => {
        if (searchValue) {
            fetchSearchCreditNoteData();
        } else {
            fetchCreditNote();
        }
    }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

    const workOrderColumns = useMemo(
        () => [
            {
                header: WorkOrderLabel.VendorId,
                accessorKey: WorkOrderKey.VendorID,
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                header: WorkOrderLabel.VendorName,
                accessorKey: WorkOrderKey.VendorName,
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                header: CreditNoteLabel.OriginalInvoiceNumber,
                accessorKey: CreditNoteLabel.OriginalInvoiceNumberAccessor,
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                header: CreditNoteLabel.OriginalInvoiceDate,
                accessorKey: CreditNoteLabel.OriginalInvoiceDateAccessor,
                enableSorting: true,
                enableColumnFilter: false,
                cell: (cell: any) => {
                    return (
                        <div className="list-group bg-none list-group-flush p-1">
                            <span>{NormalDate(cell.row?.original?.original_invoice_date)}</span>
                        </div>
                    );
                },
            },
            {
                header: CreditNoteLabel.CreditNoteNumber,
                accessorKey: CreditNoteLabel.CreditNoteNoAccessor,
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                header: CreditNoteLabel.CreditNoteDate,
                accessorKey: CreditNoteLabel.CreditNoteDateAccessor,
                enableSorting: true,
                enableColumnFilter: false,
                cell: (cell: any) => {
                    return (
                        <div className="list-group bg-none list-group-flush p-1">
                            <span>{NormalDate(cell.row?.original?.credit_note_date)}</span>
                        </div>
                    );
                },
            },
            {
                header: CreditNoteLabel.CreditNoteAmount,
                accessorKey: CreditNoteLabel.CreditNoteAmountAccessor,
                enableSorting: true,
                enableColumnFilter: false,
                cell: (cell: any) => {
                    return rupeeSymbol(cell?.row?.original?.credit_note_amount);
                },
            },
            {
                header: CreditNoteLabel.EWayBill,
                accessorKey: CreditNoteLabel.EWayBillAccessor,
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                header: ActionLabel,
                accessorKey: ActionKey,
                cell: (cell: any) => {
                    return (
                        <div className="d-flex justify-content-start enable-action flex-nowrap gap-2 icon">
                            <span className="tooltip-container">
                                <i
                                    className={`${""} mdi mdi-pencil-outline fs-4 text-info cursor-pointer`}
                                    onClick={() =>
                                        navigate(`/edit-credit-note/${cell?.row?.original?.id}`)
                                    }
                                ></i>
                                <span className="tooltip-text">{editLabel}</span>
                            </span>
                        </div>
                    );
                },
                enableSorting: false,
                enableColumnFilter: false,
            },
        ],
        []
    );

    const fetchData = (page: number) => {
        setCurrentPage(page);
    };

    const fetchSortingData = (page: number, id: string, order: string) => {
        setCurrentPage(page);
        setColumnName(id);
        setSortOrder(order);
    };

    const handleSearch = (value: string) => {
        if (value !== searchValue) {
            setCurrentPage(1);
            setSearchValue(value);
        }
    };

    return (
        <div className="page-content">
            <div className="flex-grow-1 mx-3">
                <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
                    <span className="fs-4 d-flex gap-4">{CreditNoteLabel?.CreditNoteOrderTitle} </span>
                    <div className="d-flex align-items-center gap-2">
                        <BaseButton
                            className="btn btn-success"
                            onClick={() => navigate("/add-credit-note")}
                            children={
                                <>
                                    <i className="mdi mdi-plus" /> {NewLabel}
                                </>
                            }
                        />
                    </div>
                </div>
                <div className="card py-2 px-4 overflow-x-auto">
                    {loader || creditNote?.length > 0 ? (
                        <TableContainer
                            customPageSize={pageSize}
                            setCustomPageSize={setPageSize}
                            isGlobalFilter={true}
                            columns={workOrderColumns}
                            data={searchValue ? searchCreditNote : creditNote || []}
                            tableClass="table bg-white"
                            manualPagination={true}
                            manualFiltering={true}
                            tableLoader={loader}
                            hasManualPagination={true}
                            SearchPlaceholder={Search}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            onSearch={handleSearch}
                            totalNumberOfRows={totalNumberOfRows}
                            fetchData={fetchData}
                            fetchSortingData={fetchSortingData}
                        />
                    ) : (
                        <div className="py-4 text-center">
                            <div>
                                <span className="fs-1 text-success">
                                    <RiSearchLine />
                                </span>
                            </div>
                            <div className="mt-4">
                                <h5>{notFound.dataNotFound}</h5>
                                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CreditNote;